import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { recordPageVisit } from "@/lib/api";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import { formatRelativeTime } from "@/lib/utils";
import { 
  MessageSquare, 
  Send, 
  Plus, 
  User, 
  Users,
  Loader2,
  CheckCheck,
  Clock,
  Heart
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Messages() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newContent, setNewContent] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    recordPageVisit("messages").catch(console.error);
    if (user) {
      loadConversations();
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const getToken = () => localStorage.getItem("hospice_token");

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setConversations(response.data.conversations);
    } catch (error) {
      console.error("Error loading conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMessages(response.data.messages);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/messages/conversations/${selectedConversation.id}`,
        { content: newMessage },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setNewMessage("");
      loadMessages(selectedConversation.id);
      loadConversations();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleCreateConversation = async () => {
    if (!newSubject.trim() || !newContent.trim()) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/messages/conversations`,
        { subject: newSubject, message: newContent },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setShowNewDialog(false);
      setNewSubject("");
      setNewContent("");
      loadConversations();
      toast.success("Message sent to staff");
    } catch (error) {
      toast.error("Failed to create conversation");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6" data-testid="messages-auth-required">
        <Card className="max-w-md w-full text-center glass-card border-0 shadow-soft">
          <CardContent className="p-8">
            <MessageSquare className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2">{t('messages.messageYourTeam', 'Message Your Care Team')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('messages.signInToMessage', 'Sign in to send messages directly to your hospice office staff and get responses in real-time.')}
            </p>
            <Button onClick={() => setShowAuthModal(true)} className="rounded-full">{t('messages.signInToMessage', 'Sign In to Message')}</Button>
          </CardContent>
        </Card>
        <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="messages-page">
      {/* Header */}
      <section className="bg-white border-b border-border/50 py-6">
        <div className="container-max px-6 md:px-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-2 text-foreground mb-1">{t('messages.title', 'Messages')}</h1>
              <p className="text-muted-foreground">{t('messages.subtitle', 'Communicate directly with your care team')}</p>
            </div>
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="new-message-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('messages.newMessage', 'New Message')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-serif">{t('messages.newToStaff', 'New Message to Staff')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject">{t('messages.subject', 'Subject')}</Label>
                    <Input
                      id="subject"
                      placeholder={t('messages.subjectPlaceholder', "What's this about?")}
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      data-testid="new-message-subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">{t('messages.message', 'Message')}</Label>
                    <Textarea
                      id="message"
                      placeholder={t('messages.messagePlaceholder', 'Type your message...')}
                      value={newContent}
                      onChange={(e) => setNewContent(e.target.value)}
                      rows={4}
                      data-testid="new-message-content"
                    />
                  </div>
                  <Button 
                    onClick={handleCreateConversation} 
                    className="w-full rounded-full"
                    disabled={!newSubject.trim() || !newContent.trim()}
                    data-testid="send-new-message-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {t('messages.sendMessage', 'Send Message')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Messages Interface */}
      <section className="py-6 px-6 md:px-12">
        <div className="container-max">
          <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-16rem)]">
            {/* Conversations List */}
            <Card className="glass-card border-0 shadow-soft lg:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  {t('messages.conversations', 'Conversations')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-22rem)]">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="text-center py-8 px-4">
                      <MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">{t('messages.noConversations', 'No conversations yet')}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('messages.startNew', 'Start a new message to your care team')}</p>
                    </div>
                  ) : (
                    <div className="space-y-1 p-2">
                      {conversations.map((conv) => (
                        <button
                          key={conv.id}
                          onClick={() => setSelectedConversation(conv)}
                          data-testid={`conversation-${conv.id}`}
                          className={`w-full text-left p-3 rounded-xl transition-all ${
                            selectedConversation?.id === conv.id
                              ? "bg-primary/10 text-primary"
                              : "hover:bg-muted"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{conv.subject}</p>
                              <p className="text-xs text-muted-foreground truncate mt-1">
                                {conv.last_message}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-xs text-muted-foreground">
                                {conv.last_message_time ? formatRelativeTime(conv.last_message_time) : ""}
                              </span>
                              {conv.status === "open" && (
                                <Badge variant="secondary" className="text-xs">{t('messages.open', 'Open')}</Badge>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Messages Area */}
            <Card className="glass-card border-0 shadow-soft lg:col-span-2 flex flex-col">
              {selectedConversation ? (
                <>
                  <CardHeader className="border-b border-border/50 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-serif">{selectedConversation.subject}</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {selectedConversation.staff_name ? t('messages.assignedTo', 'Assigned to {{name}}', { name: selectedConversation.staff_name }) : t('messages.waitingForStaff', 'Waiting for staff response')}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col p-0">
                    <ScrollArea className="flex-1 p-4">
                      <div className="space-y-4">
                        {messages.map((message) => (
                          <div
                            key={message.id}
                            className={`flex gap-3 ${message.sender_role === "caregiver" ? "flex-row-reverse" : ""}`}
                          >
                            <Avatar className="w-8 h-8 flex-shrink-0">
                              <AvatarFallback className={
                                message.sender_role === "caregiver"
                                  ? "bg-primary/10 text-primary"
                                  : "bg-secondary text-secondary-foreground"
                              }>
                                {message.sender_role === "caregiver" ? <User className="w-4 h-4" /> : <Heart className="w-4 h-4" />}
                              </AvatarFallback>
                            </Avatar>
                            <div className={`max-w-[75%] ${
                              message.sender_role === "caregiver"
                                ? "bg-primary text-white rounded-2xl rounded-tr-sm"
                                : "bg-muted rounded-2xl rounded-tl-sm"
                            } p-3`}>
                              <p className="text-sm">{message.content}</p>
                              <div className={`flex items-center gap-1 mt-1 ${
                                message.sender_role === "caregiver" ? "justify-end" : ""
                              }`}>
                                <span className={`text-xs ${
                                  message.sender_role === "caregiver" ? "text-white/70" : "text-muted-foreground"
                                }`}>
                                  {formatRelativeTime(message.timestamp)}
                                </span>
                                {message.sender_role === "caregiver" && message.read && (
                                  <CheckCheck className="w-3 h-3 text-white/70" />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    </ScrollArea>

                    <div className="p-4 border-t border-border/50">
                      <form onSubmit={handleSendMessage} className="flex gap-3">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={t('messages.messagePlaceholder', 'Type your message...')}
                          className="flex-1 h-11 rounded-xl"
                          disabled={sending}
                          data-testid="message-input"
                        />
                        <Button 
                          type="submit" 
                          disabled={sending || !newMessage.trim()}
                          className="h-11 px-6 rounded-xl"
                          data-testid="send-message-btn"
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </form>
                    </div>
                  </CardContent>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('messages.selectConversation', 'Select a conversation or start a new one')}</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
